import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PaginationProps {
  /** Current 1-based page. */
  page: number;
  totalPages: number;
  /** Build a URL for each page; receives the 1-based page index. */
  buildHref: (page: number) => string;
  className?: string;
}

/**
 * Compact pagination with ellipsis. Server-friendly (uses <Link>).
 */
export function Pagination({ page, totalPages, buildHref, className }: PaginationProps) {
  if (totalPages <= 1) return null;
  const pages = buildPageList(page, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-center gap-1', className)}
    >
      <PageLink
        href={buildHref(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="Page précédente"
      >
        <ChevronLeft className="h-4 w-4" />
      </PageLink>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e-${i}`} className="px-2 text-ink-muted" aria-hidden>
            …
          </span>
        ) : (
          <PageLink
            key={p}
            href={buildHref(p)}
            active={p === page}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </PageLink>
        ),
      )}
      <PageLink
        href={buildHref(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="Page suivante"
      >
        <ChevronRight className="h-4 w-4" />
      </PageLink>
    </nav>
  );
}

interface PageLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  active?: boolean;
  disabled?: boolean;
}

function PageLink({ href, active, disabled, className, children, ...rest }: PageLinkProps) {
  const cls = cn(
    'inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm font-medium',
    'transition-colors duration-250 ease-premium',
    active
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border-subtle bg-surface text-ink hover:border-primary/30 hover:bg-surface-cream',
    disabled && 'opacity-40 pointer-events-none',
    className,
  );
  if (disabled) {
    return (
      <span aria-disabled className={cls}>
        {children}
      </span>
    );
  }
  return (
    <Link href={href} className={cls} {...rest}>
      {children}
    </Link>
  );
}

function buildPageList(current: number, total: number): (number | '…')[] {
  const delta = 1;
  const range: (number | '…')[] = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);
  if (left > 2) range.push('…');
  for (let i = left; i <= right; i++) range.push(i);
  if (right < total - 1) range.push('…');
  if (total > 1) range.push(total);
  return range;
}
